/**
 * AUTO-GENERATED — DO NOT EDIT.
 * Source: sdk.manifest.yml + openapi.json.
 * Regenerate with: pnpm gen
 */


import { type QueryFunctionContext } from '@tanstack/react-query';

import { recordSnapshotLastSeq } from '../../nats/snapshot-seq-store.js';

import { fetchWithHttpTelemetry } from '../../transport/http-request.js';

import { throwHttpError } from '../../core/http-error.js';

import type { GetImagesQuotaRow, GetImageModerationStatusRow } from '../openapi-types.gen.js';

import type { EntityKeyFactory, HookSpec } from '../../types/HookSpec.js';
import type { JsonSerialisableArgs } from '../../types/JsonSerialisableArgs.js';


// ─── useGetImagesQuota ────────────────────────────────────────────────────────────

export interface useGetImagesQuotaArgs { readonly fields?: readonly ('used' | 'daily' | 'remaining' | 'resetsAt')[]; }


async function _useGetImagesQuotaQueryFn(
  args: useGetImagesQuotaArgs,
  ctx: QueryFunctionContext,
): Promise<GetImagesQuotaRow | null> {
  const url = '/v1/images/quota';
  const params = new URLSearchParams();
  // `args.fields` is a client-side projection; the API always returns the full DTO.
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useGetImagesQuota',
    routeTemplate: '/v1/images/quota',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  // A signed-out / session-expired caller gets 401 — that is a normal
  // "anonymous" state. A 403 retains its canonical error envelope.
  if (res.status === 401) return null;
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as unknown;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const data =
    json !== null && typeof json === 'object' && 'data' in json
      ? (json as { data: unknown }).data
      : json;
  return data as GetImagesQuotaRow;
}


const _useGetImagesQuotaFactory = Object.assign(
  (args: useGetImagesQuotaArgs) => ({
    queryKey: ['images', 'detail', args.fields ?? []] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useGetImagesQuotaQueryFn(args, ctx),
    staleTime: 2000,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: false,
      pollingManaged: true,
      specName: 'useGetImagesQuota',
      viewerSensitive: false,
      persist: true,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: () => ['images', 'detail'] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useGetImagesQuotaArgs, GetImagesQuotaRow>;


export const useGetImagesQuotaSpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/images/quota',
  method: 'GET' as const,
  kind: 'detail' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: false },
  entityKeys: { detail: _useGetImagesQuotaFactory },
  parseInput: (raw: unknown): useGetImagesQuotaArgs => raw as useGetImagesQuotaArgs,
  parseResponse: (raw: unknown): GetImagesQuotaRow => raw as GetImagesQuotaRow,
  specName: 'useGetImagesQuota' as const,
  polling: { hasNats: false } as const,
  viewerSensitive: false as const,
  nonpersistent: false as const,
} as unknown as HookSpec<JsonSerialisableArgs, GetImagesQuotaRow>;


// ─── useGetImageModerationStatus ────────────────────────────────────────────────────────────

export interface useGetImageModerationStatusArgs { readonly sha256: string; readonly fields?: readonly ('status' | 'classifiedAt')[]; }


async function _useGetImageModerationStatusQueryFn(
  args: useGetImageModerationStatusArgs,
  ctx: QueryFunctionContext,
): Promise<GetImageModerationStatusRow | null> {
  if (args.sha256 === undefined || args.sha256 === null || args.sha256 === '') return null;
  const url = `/v1/images/${args.sha256}/moderation-status`;
  const params = new URLSearchParams();
  // `args.fields` is a client-side projection; the API always returns the full DTO.
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useGetImageModerationStatus',
    routeTemplate: '/v1/images/{sha256}/moderation-status',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  // A signed-out / session-expired caller gets 401 — that is a normal
  // "anonymous" state. A 403 retains its canonical error envelope.
  if (res.status === 401) return null;
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as unknown;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const data =
    json !== null && typeof json === 'object' && 'data' in json
      ? (json as { data: unknown }).data
      : json;
  return data as GetImageModerationStatusRow;
}


const _useGetImageModerationStatusFactory = Object.assign(
  (args: useGetImageModerationStatusArgs) => ({
    queryKey: ['images', 'detail', args.sha256, 'moderation-status'] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useGetImageModerationStatusQueryFn(args, ctx),
    staleTime: 2000,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: false,
      pollingManaged: true,
      specName: 'useGetImageModerationStatus',
      viewerSensitive: false,
      persist: true,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: (id: string) => ['images', 'detail', id] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useGetImageModerationStatusArgs, GetImageModerationStatusRow>;


export const useGetImageModerationStatusSpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/images/{sha256}/moderation-status',
  method: 'GET' as const,
  kind: 'detail' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: true },
  entityKeys: { detail: _useGetImageModerationStatusFactory },
  parseInput: (raw: unknown): useGetImageModerationStatusArgs => raw as useGetImageModerationStatusArgs,
  parseResponse: (raw: unknown): GetImageModerationStatusRow => raw as GetImageModerationStatusRow,
  specName: 'useGetImageModerationStatus' as const,
  polling: { hasNats: false, intervalMs: false } as const,
  viewerSensitive: false as const,
  nonpersistent: false as const,
} as unknown as HookSpec<JsonSerialisableArgs, GetImageModerationStatusRow>;
